import {isSketchRelationContainer} from '../../solver/constraint/SketchRelationContainer';
import {resolveSketchRelationDisplayName} from '../../solver/constraint/relation-compatibility/SketchRelationDefinitions';
import {SketchRelationKind} from '../../solver/constraint/relation-compatibility/SketchRelationKinds';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchLineObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type {ISketchObject} from '../../../mesh/sketch/ISketchObject';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import type {
    SketchDimensionRelationViewProjection,
    SketchDimensionRelationViewSyncResult,
} from '../../../../types/sketch/dimension/SketchDimensionRelationViewTypes';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';
import type {SketchDimensionSnapshot} from '../SketchDimensionTypes';

/** 尺寸派生关系视图在容器内使用的领域来源 ID。 */
const DIMENSION_RELATION_VIEW_SOURCE_ID = 'sketch-dimensions';

/**
 * 尺寸关系只读视图同步器。
 *
 * 设计边界：
 * 1. 尺寸仓库是数值、角色、分支和引用的唯一事实源；
 * 2. 本类只投影 `SketchRelationSnapshot`，不实例化任何 `SketchRelationDefinition`；
 * 3. 派生视图不进入参数公式采集、历史关系快照或 `.puppy` 持久化；
 * 4. 关系面板可显示与 SOLIDWORKS 一致的“距离/角度/半径/直径”语义，但必须按只读视图处理。
 */
export class SketchDimensionRelationViewSynchronizer {
    /**
     * 按尺寸仓库当前状态对账指定草图的只读关系视图。
     *
     * @param sketchId 草图持久 ID。
     * @param sketchGroup 运行时草图组。
     * @param repository 尺寸仓库。
     * @returns 新增、更新与移除视图数量。
     */
    public synchronize(
        sketchId: string,
        sketchGroup: SketchGroup,
        repository: SketchDimensionRepository,
    ): SketchDimensionRelationViewSyncResult {
        const projections = repository.listBySketch(sketchId)
            .map((dimension) => dimension.toSnapshot())
            .map((snapshot) => this.resolveProjection(snapshot, sketchGroup))
            .filter((projection): projection is SketchDimensionRelationViewProjection => projection !== null);
        const snapshotsByTarget = new Map<object, SketchDimensionRelationViewProjection['snapshot'][]>();

        projections.forEach((projection) => {
            projection.targets.forEach((target) => {
                const snapshots = snapshotsByTarget.get(target) ?? [];

                snapshots.push(projection.snapshot);
                snapshotsByTarget.set(target, snapshots);
            });
        });

        let attached = 0;
        let updated = 0;
        let detached = 0;
        const visitedContainers = new Set<object>();

        sketchGroup.traverse((object) => {
            const container = Reflect.get(object, 'constraints');

            if (!isSketchRelationContainer(container) || visitedContainers.has(container)) {
                return;
            }
            visitedContainers.add(container);

            const previous = container.getRelationSnapshots()
                .filter((snapshot) => snapshot.isDerived && snapshot.sourceDimensionId);
            const next = snapshotsByTarget.get(object) ?? [];
            const previousByDimensionId = new Map(
                previous.map((snapshot) => [snapshot.sourceDimensionId!, snapshot]),
            );
            const nextByDimensionId = new Map(
                next.map((snapshot) => [snapshot.sourceDimensionId!, snapshot]),
            );

            nextByDimensionId.forEach((snapshot, dimensionId) => {
                const existing = previousByDimensionId.get(dimensionId);

                if (!existing) {
                    attached += 1;
                } else if (existing.relationType !== snapshot.relationType
                    || existing.baseDisplayName !== snapshot.baseDisplayName
                    || existing.name !== snapshot.name) {
                    updated += 1;
                }
            });
            previousByDimensionId.forEach((_snapshot, dimensionId) => {
                if (!nextByDimensionId.has(dimensionId)) {
                    detached += 1;
                }
            });

            container.setDerivedRelationSnapshots(DIMENSION_RELATION_VIEW_SOURCE_ID, next);
        });

        return {attached, updated, detached};
    }

    /**
     * 把一个活动 driving 尺寸投影为关系面板视图。
     *
     * @param snapshot 尺寸快照。
     * @param sketchGroup 草图组。
     * @returns 投影视图；从动、抑制、悬空或不支持的尺寸返回 `null`。
     */
    private resolveProjection(
        snapshot: SketchDimensionSnapshot,
        sketchGroup: SketchGroup,
    ): SketchDimensionRelationViewProjection | null {
        if (snapshot.role !== 'driving' || snapshot.suppressed || snapshot.repair !== null) {
            return null;
        }

        switch (snapshot.dimensionKind) {
            case 'length': {
                const line = this.resolveObject(snapshot, 0, sketchGroup);

                return line && isSketchLineObject(line)
                    ? this.createProjection(snapshot, [line.startPoint, line.endPoint], SketchRelationKind.Distance)
                    : null;
            }
            case 'point-distance': {
                const first = this.resolveObject(snapshot, 0, sketchGroup);
                const second = this.resolveObject(snapshot, 1, sketchGroup);

                return first && second && isSketchPointObject(first) && isSketchPointObject(second)
                    ? this.createProjection(snapshot, [first, second], SketchRelationKind.Distance)
                    : null;
            }
            case 'radius':
            case 'diameter': {
                const target = this.resolveObject(snapshot, 0, sketchGroup);

                if (target && isSketchArcObject(target)) {
                    return this.createProjection(snapshot, [target], SketchRelationKind.Radius);
                }
                if (target && isSketchCircleObject(target)) {
                    return this.createProjection(snapshot, [target], SketchRelationKind.Diameter);
                }
                return null;
            }
            case 'three-point-angle': {
                const vertex = this.resolveObject(snapshot, 0, sketchGroup);
                const firstRay = this.resolveObject(snapshot, 1, sketchGroup);
                const secondRay = this.resolveObject(snapshot, 2, sketchGroup);

                return vertex && firstRay && secondRay
                    && isSketchPointObject(vertex)
                    && isSketchPointObject(firstRay)
                    && isSketchPointObject(secondRay)
                    ? this.createProjection(
                        snapshot,
                        [vertex, firstRay, secondRay],
                        SketchRelationKind.ThreePointAngle,
                    )
                    : null;
            }
            case 'spline-length': {
                const spline = this.resolveObject(snapshot, 0, sketchGroup);

                return spline && (isSketchSplineObject(spline) || isSketchStyleSplineObject(spline))
                    ? this.createProjection(snapshot, [spline], SketchRelationKind.Distance)
                    : null;
            }
            case 'angle': {
                const first = this.resolveObject(snapshot, 0, sketchGroup);
                const second = this.resolveObject(snapshot, 1, sketchGroup);

                return first && second && isSketchLineObject(first) && isSketchLineObject(second)
                    ? this.createProjection(snapshot, [first, second], SketchRelationKind.Angle)
                    : null;
            }
            default:
                return null;
        }
    }

    /**
     * 创建一条不携带尺寸数值的只读关系快照。
     *
     * @param dimension 尺寸快照。
     * @param targets 展示目标。
     * @param relationType 关系面板语义类型。
     * @returns 只读投影。
     */
    private createProjection(
        dimension: SketchDimensionSnapshot,
        targets: readonly ISketchObject[],
        relationType: SketchRelationKind,
    ): SketchDimensionRelationViewProjection {
        const displayName = resolveSketchRelationDisplayName(relationType);

        return {
            targets,
            snapshot: {
                id: this.createStableViewId(dimension.dimensionId),
                name: displayName,
                relationType,
                baseDisplayName: displayName,
                isDerived: true,
                isReadOnly: true,
                sourceDimensionId: dimension.dimensionId,
            },
        };
    }

    /**
     * 从尺寸稳定 ID 派生负数运行时视图 ID。
     *
     * 普通关系 ID 为递增正数，负数域专供只读派生视图，避免删除入口误命中真实关系。
     * 该 ID 不持久化，只要求同一尺寸在不同目标容器和重复对账中保持一致。
     *
     * @param dimensionId 尺寸稳定 ID。
     * @returns 负数视图 ID。
     */
    private createStableViewId(dimensionId: string): number {
        let hash = 2166136261;

        for (let index = 0; index < dimensionId.length; index += 1) {
            hash ^= dimensionId.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }

        return -((hash >>> 0) % 2147483646 + 1);
    }

    /**
     * 按引用选择顺序解析运行时草图对象。
     *
     * @param snapshot 尺寸快照。
     * @param selectionOrder 选择顺序。
     * @param sketchGroup 草图组。
     * @returns 匹配对象；无法解析时返回 `null`。
     */
    private resolveObject(
        snapshot: SketchDimensionSnapshot,
        selectionOrder: number,
        sketchGroup: SketchGroup,
    ): ISketchObject | null {
        const reference = snapshot.references.find((item) => item.selectionOrder === selectionOrder);

        if (!reference) {
            return null;
        }

        const target = (reference.pointId ? sketchGroup.getObjectByPersistentId(reference.pointId) : null)
            ?? sketchGroup.getObjectByPersistentId(reference.entityId);

        return (target as ISketchObject | null) ?? null;
    }
}
